import { observer } from 'mobx-react-lite'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Segment } from '../../../features/segment'
import { useAuth } from '../../hooks/useAuth'
import { styled } from '../../lib/stitches'
import Accordion from '../core/Accordion'
import { AnnouncementHeader } from '../core/AnnouncementHeader'
import Button from '../core/Button/Button'
import Text from '../core/Text/Text'
import Ticker from '../platform/dashboard/Ticker'
import Footer from './components/Footer'
import FreeInBrowser from './components/FreeInBrowser'
import Guaranteed from './components/Guaranteed'
import Topbar from './components/Topbar'

export const segmentMarketingPage = (button: string) => {
  window.analytics.track('Button Clicked', {
    button: button,
    location: 'marketing page',
    source: window.location.pathname
  })
}
const MarketingPageContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
})

const FaqWrapper = styled('section', {
  width: '100%',
  backgroundImage: 'url(/static/images/gradient-faq.png)',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover'
})

const FaqContent = styled('section', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '800px',
  padding: '72px 24px 62px',
  margin: '0 auto',
  '@tablet': {
    padding: '64px 24px 170px'
  }
})

const HeaderSection = styled('div', {
  backgroundImage: 'url(/static/images/grainless-gradient.png)',
  backgroundPosition: 'bottom',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '72px 0 60px',
  marginTop: '-72px',
  '@tablet': {
    marginTop: '-96px',
    padding: '100px 0 112px'
  }
})

const HeadingContainer = styled('div', {
  maxWidth: '312px',
  '@tablet': {
    maxWidth: '640px'
  }
})

const Box = styled('div', {
  display: 'flex',
  alignItems: 'center',
  textAlign: 'center'
})

const Span = styled('span', {
  display: 'inline-block',
  fontWeight: '$regular',
  fontSize: '67px',
  fontFamily: 'Radley',
  fontStyle: 'italic',
  letterSpacing: '-0.05em',
  lineHeight: '67px',
  color: '$gray01',
  textShadow: '0px 0px 8.93849px rgba(0, 0, 0, 0.25)',
  '@tablet': {
    fontSize: '142px',
    lineHeight: '142px'
  }
})

const ImgWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'center'
})

const Img = styled('img', {
  width: '364px',
  '@tablet': {
    width: '900px'
  },
  '@laptop': {
    width: '1213px'
  }
})

const ButtonWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  margin: '32px 0 64px',
  '@tablet': {
    margin: '30px 0 112px'
  }
})

const MarketingPage = () => {
  const navigate = useNavigate()
  const faqRef = useRef(null)
  const { user } = useAuth()
  useEffect(() => {
    if (user) navigate('/home')
  }, [user])
  return (
    <Segment.ViewTracker attributes={{ path: '/' }}>
      <MarketingPageContainer>
        <AnnouncementHeader />
        <Topbar faqRef={faqRef} />
        <HeaderSection>
          <HeadingContainer>
            <Box>
              <Text
                fontWeight="semibold"
                css={{
                  fontSize: '58px',
                  letterSpacing: '-0.08em',
                  lineHeight: '60px',
                  marginTop: '100px',
                  color: '$gray15',
                  '@tablet': {
                    fontSize: '114px',
                    lineHeight: '114px'
                  }
                }}
              >
                Your place to
                <br />
                make&nbsp;
                <Span>music</Span>
              </Text>
            </Box>
            <ButtonWrapper>
              <Button
                type="outlined"
                onClick={() => {
                  segmentMarketingPage('Explore Sounds')
                  navigate('/home/songs')
                }}
                css={{
                  marginRight: '8px',
                  fontWeight: 500,
                  padding: '0 16px',
                  height: '40px',
                  fontSize: '16px',
                  borderRadius: '16px',
                  color: '$gray12',
                  '@tablet': {
                    padding: '0 31px',
                    height: '64px',
                    fontSize: '18px'
                  }
                }}
              >
                Explore sounds
              </Button>
              <Button
                type={'dark'}
                onClick={() => {
                  segmentMarketingPage('Start Creating')
                  navigate('/studio')
                }}
                css={{
                  padding: '0 16px',
                  fontWeight: 500,
                  height: '40px',
                  fontSize: '16px',
                  borderRadius: '16px',
                  '@tablet': {
                    padding: '0 31px',
                    height: '64px',
                    fontSize: '18px'
                  }
                }}
              >
                Start creating
              </Button>
            </ButtonWrapper>
          </HeadingContainer>
          <ImgWrapper>
            <Img src="/static/images/mockup.png" alt="mockup" height="100%" />
          </ImgWrapper>
        </HeaderSection>
        <FreeInBrowser />
        <Ticker variant="dark" />
        <Guaranteed />
        <FaqWrapper ref={faqRef} id="faq">
          <FaqContent>
            <Text
              css={{
                fontSize: '56px',
                fontWeight: 600,
                lineHeight: '56px',
                marginBottom: '22px'
              }}
            >
              F.A.Q
            </Text>
            <div>
              <Accordion key="What is Arpeggi Studio?" heading="What is Arpeggi Studio?">
                <>
                  Arpeggi Studio is a collaborative web3 music creation platform built on the ARP
                  protocol. Over the years music has become increasingly 'single player', Arpeggi
                  Studio automates the trust in digital collaboration so artists can be more
                  connected than ever.
                  <br />
                  <br />
                  Arpeggi Studio allows musicians to share their music for reuse with guaranteed
                  attribution when their sound is used. In the traditional music industry,
                  compliantly using someone else’s song or sound requires payment and legal
                  clearance. With Arpeggi, any sound you hear is yours to create with.
                  <br />
                  <br />
                  Today's platform features an infinite community sourced sample library to create
                  with and a full featured DAW to mint your own songs and samples as shareable
                  tokens.
                </>
              </Accordion>
              <Accordion
                key="How does Arpeggi Studio work?"
                heading="How does Arpeggi Studio work?"
              >
                <>
                  Arpeggi Studio is built on top of an infinite and decentralized sound library, the
                  'Audio Relationship Protocol' (ARP). When a song is made in Arpeggi Studio, the
                  song is registered back into the ARP protocol with full audio and authorship
                  information. Authorship includes the creator of the new song, but also all the
                  references to the sounds the song used, creating new connections between song
                  authors and the sample contributors of the song.
                  <br />
                  <br />
                  For more on ARP check out our documentation
                  <a
                    href="https://docs.arpeggi.io/faq/build-on-arpeggi"
                    style={{ fontWeight: 600 }}
                    target="_blank"
                  >
                    &nbsp;here
                  </a>
                </>
              </Accordion>
              <Accordion
                key="How is Arpeggi Studio different from other platforms?"
                heading="How is Arpeggi Studio different from other platforms?"
              >
                <>
                  Arpeggi Studio reimagines traditional web2 music platforms by combining creation
                  and consumption - imagine if Spotify allowed you to take your favorite song to
                  sample and remix all inside the app.
                  <br />
                  <br />
                  Most other web3 platforms focus on changing the way we own music. Arpeggi Studio
                  focuses on changing the way we create music.
                </>
              </Accordion>
              <Accordion
                key="How much does Arpeggi Studio cost?"
                heading="How much does Arpeggi Studio cost?"
              >
                Arpeggi Studio is completely free!
              </Accordion>
            </div>
          </FaqContent>
        </FaqWrapper>
        <Footer />
        <Text
          fontWeight="medium"
          type="primary_inverted"
          css={{
            width: '100%',
            background: '$black',
            padding: '22px 0 20px',
            fontSize: '17px',
            letterSpacing: '-1px',
            lineHeight: '24px',
            textAlign: 'center',
            fontFamily: '$mono',
            position: 'sticky',
            bottom: 0,
            '@tablet': {
              display: 'none'
            }
          }}
        >
          Login on desktop to use our studio
        </Text>
      </MarketingPageContainer>
    </Segment.ViewTracker>
  )
}
export default observer(MarketingPage)
